import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import translations from "../../locals/translations.json";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { getUserBids } from "../../services/auctionServices.ts";
import { useCurrencyUtils } from "../../utils/currencyUtils";
import api from "../../lib/api.ts";

const MyBids = () => {
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  const { user } = useAuth();
  const t = translations[language].myBids;

  // Currency utils
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();

  // State
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [activeTab, setActiveTab] = useState("bids"); // "bids", "prebids", "underApproval", "liveBid"
  const [expandedVehicles, setExpandedVehicles] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  const isCorporateBuyer = user?.buyer?.parentId === null;

  // Additional State for child buyers
  const [childBuyers, setChildBuyers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);

  useEffect(() => {
    const fetchChildBuyers = async () => {
      try {
        const response = await api.get("/users/corporatebuyers");
        setChildBuyers(response.data.data || []);
      } catch (error) {
        console.error("Error fetching child buyers:", error);
      }
    };
    fetchChildBuyers();
  }, []);

  // Load bids
  const loadAllBids = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError("");

      const bidsResponse = await getUserBids(selectedUserId ?? undefined);
      let allBids = [];

      if (bidsResponse.success) {
        allBids = bidsResponse.data || bidsResponse.bids || [];
      }
      // // Filter bids for selected buyer
      // if (selectedUserId) {
      //   allBids = allBids.filter((bid) => bid.userId === selectedUserId);
      // }
      setBids(allBids);
    } catch (err) {
      console.error("Error loading bids:", err);
      setError("Failed to load bids. Please try again.");
      setBids([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllBids();
  }, [user?.id, selectedUserId]);

  // Toggle expansion
  const toggleVehicleExpansion = (vehicleId) => {
    setExpandedVehicles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(vehicleId)) newSet.delete(vehicleId);
      else newSet.add(vehicleId);
      return newSet;
    });
  };

  // Get filtered data by tab
  const getDisplayData = () => {
    if (activeTab === "prebids") {
      return bids.filter((bid) => bid.isPrebid === true) || [];
    }
    if (activeTab === "underApproval") {
      return bids.filter((bid) => bid.isWinningBid === true) || [];
    }
    if (activeTab === "liveBid") {
      return bids.filter((bid) => !bid.isPrebid) || [];
    }
    // "bids" = show all
    return bids || [];
  };

  // Group bids by vehicle + apply filters
  const getGroupedBids = () => {
    const grouped = {};
    const currentData = getDisplayData();

    currentData.forEach((bid) => {
      const vehicleId = bid.vehicleId || bid.vehicle?.id;
      if (!vehicleId) return;

      const lotNumber = bid.vehicle?.lotNumber || "";
      if (
        searchTerm &&
        !lotNumber.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return;
      }

      if (dateFilter) {
        const bidDate = new Date(bid.createdAt).toISOString().split("T")[0];
        if (bidDate !== dateFilter) return;
      }

      if (!grouped[vehicleId]) {
        grouped[vehicleId] = {
          vehicleId,
          vehicleName: bid.vehicle?.name || "Unknown Vehicle",
          lotNo: bid.vehicle?.lotNumber || "-",
          auctionName: bid.auctionLane?.auction?.name || "-",
          latestBid: bid.vehicle?.latestBid || 0,
          totalBids: 0,
          bids: [],
        };
      }

      grouped[vehicleId].bids.push(bid);
      grouped[vehicleId].totalBids++;
    });

    // Sort bids inside each group (latest first)
    Object.values(grouped).forEach((group) => {
      group.bids.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    });

    // Sort groups by latest bid time
    return Object.values(grouped).sort((a, b) => {
      const timeA = a.bids[0] ? new Date(a.bids[0].createdAt) : new Date(0);
      const timeB = b.bids[0] ? new Date(b.bids[0].createdAt) : new Date(0);
      return timeB - timeA;
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setExpandedVehicles(new Set());
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setDateFilter("");
    setExpandedVehicles(new Set());
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const groupedBids = getGroupedBids();

  return (
    <motion.div
      className="bg-card rounded-[8px] sm:rounded-md shadow overflow-x-auto mx-0 sm:mx-1 md:mx-0"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <div className="bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft gap-3 min-h-[72px]">
          <div className="flex flex-col justify-center">
            <h2 className="text-white text-base sm:text-lg font-semibold whitespace-nowrap">
              {t.heading}
            </h2>
          </div>

          <div className="flex items-center">
            {childBuyers.length > 0 && (
              <select
                value={selectedUserId ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  const parsed = Number(val);

                  if (val === "" || Number.isNaN(parsed)) {
                    setSelectedUserId(null);
                  } else {
                    setSelectedUserId(parsed);
                  }
                }}
                className="bg-page p-2 mr-2 rounded-full text-white text-xs focus:outline-none"
              >
                <option value="">Select Buyer</option>

                {childBuyers.map((child) => {
                  const userId = child.user?.userId ?? child.user?.id;
                  return (
                    <option key={userId ?? child.buyerId} value={userId ?? ""}>
                      {child.user?.name || child.businessName || "-"}
                      {child.user?.clientId ? ` (${child.user.clientId})` : ""}
                    </option>
                  );
                })}
              </select>
            )}

            <nav
              className={`flex gap-1.5 sm:gap-2 ${isRTL ? "flex-row-reverse" : ""}`}
            >
              {[
                { key: "bids", label: t.buttons.bidHistory },
                { key: "liveBid", label: t.buttons.liveBids },
                { key: "prebids", label: t.buttons.preBid },
                { key: "underApproval", label: t.buttons.underApproval },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`flex-shrink-0 px-3 sm:px-4 py-1.5 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 whitespace-nowrap ${
                    activeTab === tab.key
                      ? "bg-white text-primary shadow-md"
                      : "bg-black/10 text-white/80 hover:bg-white/10 hover:text-white border border-white/5"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Filters & Controls */}
      <div>
        <div className="bg-card p-4 rounded-lg border border-default mb-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-end gap-3">
 

            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full sm:w-44 p-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary text-black"
              placeholder={t.filters.dateFrom}
            />

            <input
              type="text"
              placeholder={t.filters.searchHere}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-64 p-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />

            <div className="flex gap-3">
              <button
                onClick={handleClearFilters}
                className="px-5 py-2.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition text-sm font-medium"
              >
                {t.buttons.clear}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="block md:hidden space-y-4">
          {loading ? (
            <div className="text-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div>
              <p className="mt-3 text- text-white">Loading bids...</p>
            </div>
          ) : error ? (
            <div className="text-center py-10 text-red-600">{error}</div>
          ) : groupedBids.length === 0 ? (
            <div className="text-center py-10 text-description">
              No bids found
            </div>
          ) : (
            groupedBids.map((group) => {
              const bidder = group.bids[0]?.bidder;
              const isChildRecord = bidder?.buyer?.parentId != null;

              return (
                <div
                  key={group.vehicleId}
                  className={`rounded-lg shadow border border-gray-800/50 overflow-hidden ${
                    isChildRecord ? "bg-gray-900/40" : "bg-page"
                  }`}
                >
                  <div
                    className={`px-4 py-3 border-b border-gray-800/50 flex justify-between items-center ${
                      isChildRecord ? "bg-gray-900/50" : "bg-gray-800/30"
                    }`}
                  >
                    <h3 className="font-semibold text-gray-900 truncate max-w-[70%]">
                      {group.vehicleName}
                    </h3>
                    <button
                      onClick={() => toggleVehicleExpansion(group.vehicleId)}
                      className="text-sm text-primary hover:underline"
                    >
                      {expandedVehicles.has(group.vehicleId) ? "Hide" : "View"}{" "}
                      Bids
                    </button>
                  </div>

                  <div className="p-4">
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Lot No:</span>
                        <span className="font-medium">{group.lotNo}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/70">Bid By</span>
                        <div className="text-right">
                          <span className="font-medium text-white block">
                            {childBuyers.length > 0 ? (
                              <>
                                {group.bids[0]?.bidder?.name || "-"}{" "}
                                {group.bids[0]?.bidder?.clientId
                                  ? `(${group.bids[0]?.bidder?.clientId})`
                                  : ""}
                              </>
                            ) : (
                              group.bids[0]?.bidder?.name || "-"
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Latest Bid:</span>
                        <span className="font-semibold text-primary">
                          {displayPriceWithSymbol(
                            convertPrice(Number(group.latestBid || 0)),
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Bids:</span>
                        <span className="font-medium">{group.totalBids}</span>
                      </div>
                    </div>

                    {expandedVehicles.has(group.vehicleId) && (
                      <div className="border-t pt-4 mt-2">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="py-2 px-3 text-left">Date</th>
                              <th className="py-2 px-3 text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.bids.map((bid, idx) => (
                              <tr key={idx} className="border-b">
                                <td className="py-2 px-3 text-gray-600">
                                  {formatDate(bid.createdAt)}
                                </td>
                                <td className="py-2 px-3 text-right font-medium text-primary">
                                  {displayPriceWithSymbol(
                                    convertPrice(Number(bid.amount || 0)),
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto shadow border border-default rounded-lg">
          <table className="w-full border-collapse text-sm min-w-[900px]">
            <thead>
              <tr className="bg-page text-left text-white">
                {t.tableHeaders.map((head, i) => (
                  <th key={i} className="px-4 py-3 text-left font-medium">
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={t.tableHeaders.length}
                    className="text-center py-10"
                  >
                    <div className="flex justify-center items-center gap-3">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      <span className="text-white">Loading bids...</span>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan={t.tableHeaders.length}
                    className="text-center py-10 text-red-600"
                  >
                    {error}
                  </td>
                </tr>
              ) : groupedBids.length === 0 ? (
                <tr>
                  <td
                    colSpan={t.tableHeaders.length}
                    className="text-center py-10 text-description"
                  >
                    No bids found
                  </td>
                </tr>
              ) : (
                groupedBids.map((group) => {
                  const isChildRecord =
                    !!group.bids[0]?.bidder?.buyer?.parentId;
                  return (
                    <React.Fragment key={group.vehicleId}>
                      {/* Main Vehicle Row */}
                      <tr
                        className={`border-b transition-colors ${
                          isChildRecord
                            ? "bg-amber-50 hover:bg-amber-100"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-3 font-medium">
                          <div className="flex items-center gap-3 text-black ">
                            <button
                              onClick={() =>
                                toggleVehicleExpansion(group.vehicleId)
                              }
                              className="text-lg text-primary hover:text-primary-dark"
                            >
                              {expandedVehicles.has(group.vehicleId)
                                ? "▼"
                                : "▶"}
                            </button>
                            <span
                              className="line-clamp-2 max-w-xs"
                              title={group.vehicleName}
                            >
                              {group.vehicleName}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-black">
                          {group.lotNo}
                        </td>
                        <td className="px-4 py-3 text-center text-black">
                          {group.totalBids}
                        </td>
                        <td className="px-4 py-3 text-black">
                          <div
                            className="truncate max-w-xs"
                            title={group.auctionName}
                          >
                            {group.auctionName}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center font-semibold text-primary">
                          {displayPriceWithSymbol(
                            convertPrice(Number(group.latestBid || 0)),
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-black">-</td>
                        <td className="px-4 py-3 text-center text-black">
                          {formatDate(group.bids[0]?.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-center text-black whitespace-nowrap">
                          {group.bids[0]?.bidder?.buyer?.user ? (
                            <div className="flex flex-col items-center">
                              <span className="font-medium">
                                {group.bids[0].bidder.buyer.user.name || "-"}
                              </span>
                              {group.bids[0].bidder.buyer.user.clientId && (
                                <span className="text-xs text-gray-500">
                                  ({group.bids[0].bidder.buyer.user.clientId})
                                </span>
                              )}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>

                      {/* Expandable Bid Details */}
                      {expandedVehicles.has(group.vehicleId) &&
                        group.bids.map((bid, bidIndex) => (
                          <tr
                            key={`${group.vehicleId}-${bid.id || bidIndex}`}
                            className="bg-gray-50 border-b hover:bg-gray-100"
                          >
                            <td className="px-4 py-3 pl-12 text-gray-600">
                              {/* Optional: show Prebid / Live badge if needed */}
                            </td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3 text-right font-medium text-primary">
                              {bidIndex === 0
                                ? displayPriceWithSymbol(
                                    convertPrice(Number(group.latestBid || 0)),
                                  )
                                : displayPriceWithSymbol(
                                    convertPrice(Number(bid.amount || 0)),
                                  )}
                            </td>
                            <td className="px-4 py-3 text-center text-gray-600">
                              {new Date(bid.createdAt).toLocaleTimeString(
                                "en-US",
                                {
                                  hour12: false,
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  second: "2-digit",
                                },
                              )}
                            </td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
};

export default MyBids;
